import { oc } from "@orpc/contract";
import { z } from "zod";
import { RelayDeliveryTargetOverrideSchema } from "@erp/shared-types";

/**
 * Sync Relay integrations — the per-query definitions the Windows sync agent
 * polls and runs against a customer's Oracle database.
 *
 * The agent owns creating and editing these over its API-key-authenticated REST
 * routes (`/api/v1/relay/integration`), because it cannot consume oRPC
 * contracts. These procedures are the ERP-side read + upload-target linkage, so
 * an operator can point an integration at an S3 sink from the product instead of
 * from the agent box.
 */

export const SyncRelayIntegrationSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  /** Sync type, e.g. "customers" — selects the field mapping the ingest uses. */
  type: z.string(),
  name: z.string(),
  enabled: z.boolean(),
  /** Cron expression the agent schedules against. */
  schedule: z.string(),
  /** Linked upload-target DataSource; null falls back to the RELAY_S3_* env target. */
  dataSourceId: z.string().uuid().nullable(),
  /**
   * Per-integration delivery path, or null to inherit the agent's own setting.
   *
   * Null is not "api": the agent-wide switch remains the kill switch for a whole
   * box, so an inheriting integration follows whatever that machine is set to.
   */
  deliveryTarget: RelayDeliveryTargetOverrideSchema,
  /** Incremental cursor: the last row's sync timestamp, and its tiebreak id. */
  lastSyncTime: z.string().nullable(),
  lastSyncId: z.string().nullable(),
  /** When a sync last completed, regardless of whether it moved the cursor. */
  lastSyncedAt: z.string().nullable(),
});

export const SyncRelayUploadTargetSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  bucket: z.string(),
  region: z.string(),
  basePath: z.string().nullable(),
  /**
   * Why this DataSource cannot be linked, or null when it can. Unusable
   * candidates are returned rather than filtered out so an operator can see
   * *why* the sink they just created is not selectable.
   */
  ineligibleReason: z.string().nullable(),
});

export const SetSyncRelayDataSourceInputSchema = z.object({
  id: z.string().uuid(),
  /** null unlinks, reverting the integration to the env-configured target. */
  dataSourceId: z.string().uuid().nullable(),
});

export const SetSyncRelayDeliveryTargetInputSchema = z.object({
  id: z.string().uuid(),
  /** null clears the override so the integration follows the agent again. */
  deliveryTarget: RelayDeliveryTargetOverrideSchema,
});

/**
 * Result of actually exercising an integration's upload target: mint real
 * credentials, start a multipart upload under the real prefix, abort it.
 *
 * Structural validation only proves a credential is *named*. Minting only proves
 * `sts:GetFederationToken` — it never consults the S3 statement. So without this,
 * the first thing to exercise write permission is the agent, minutes later, on a
 * remote machine.
 */
export const TestSyncRelayUploadTargetResponseSchema = z.object({
  ok: z.boolean(),
  /** Operator-facing result: what worked, or which misconfiguration to fix. */
  message: z.string(),
  /** Where the check wrote, so the prefix can be eyeballed against IAM policy. */
  bucket: z.string().nullable(),
  region: z.string().nullable(),
  prefix: z.string().nullable(),
});

export const syncRelayContracts = {
  list: oc
    .input(z.object({}))
    .output(z.object({ integrations: z.array(SyncRelayIntegrationSchema) }))
    .route({
      summary: "List sync relay integrations",
      description:
        "Lists the organization's sync relay queries the on-premises sync agent runs against the ERP database, with type, schedule, enabled flag, upload target, delivery path and last sync cursor.",
      method: "GET",
    }),
  listUploadTargets: oc
    .input(z.object({}))
    .output(z.object({ targets: z.array(SyncRelayUploadTargetSchema) }))
    .route({
      summary: "List sync relay upload targets",
      description:
        "Lists S3 data sources a sync relay integration can upload to, each with bucket, region, base path and the reason it is ineligible, if any.",
      method: "GET",
    }),
  setDataSource: oc
    .input(SetSyncRelayDataSourceInputSchema)
    .output(z.object({ integration: SyncRelayIntegrationSchema }))
    .route({
      summary: "Set a sync relay upload target",
      description:
        "Links a sync relay integration to an S3 data source, or unlinks it to fall back to the default target. Validates eligibility and records a version entry.",
      method: "POST",
    }),
  setDeliveryTarget: oc
    .input(SetSyncRelayDeliveryTargetInputSchema)
    .output(z.object({ integration: SyncRelayIntegrationSchema }))
    .route({
      summary: "Set a sync relay delivery path",
      description:
        "Overrides how one sync relay integration delivers data, or clears the override so it follows the sync agent's own setting. Records a version entry.",
      method: "POST",
    }),
  // POST, not GET: this mints real credentials and writes to S3.
  testUploadTarget: oc
    .input(z.object({ id: z.string().uuid() }))
    .output(TestSyncRelayUploadTargetResponseSchema)
    .route({
      summary: "Test a sync relay upload target",
      description:
        "Mints real upload access for an integration's target and starts then aborts a multipart upload to prove write permission. Returns ok, message, bucket, region and prefix.",
      method: "POST",
    }),
} as const;

export type SyncRelayContracts = typeof syncRelayContracts;
export type SyncRelayIntegration = z.infer<typeof SyncRelayIntegrationSchema>;
export type SyncRelayUploadTarget = z.infer<typeof SyncRelayUploadTargetSchema>;
export type SetSyncRelayDataSourceInput = z.infer<
  typeof SetSyncRelayDataSourceInputSchema
>;
export type SetSyncRelayDeliveryTargetInput = z.infer<
  typeof SetSyncRelayDeliveryTargetInputSchema
>;
export type TestSyncRelayUploadTargetResponse = z.infer<
  typeof TestSyncRelayUploadTargetResponseSchema
>;
